export { API_BASE_URL, authApi } from './auth';
export type { User } from './auth';
export type { Submission, SubmissionSummary, Module, Requirement, ValidationStatus } from './submissionApi';
export type { TextHighlight, TextLocationsResponse } from './fileApi';
export type { ChatSession, ChatHistoryMessage, ChatHistoryResponse } from './chatApi';

export interface DocumentRow {
  document_id: string;
  req_code: string;
  label: string;
  mapped_document_id: string;
  filename: string;
  status: string;
  module: string;
  submission_id: string;
  submission_name: string;
  submission_type: string;
}

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'submitter' | 'viewer';
  is_active: boolean;
  created_at: string;
}

export interface CreateUserPayload {
  name: string;
  email: string;
  role: string;
  temp_password: string;
}

export interface UpdateUserPayload {
  name?: string;
  role?: string;
}

import { API_BASE_URL } from './auth';
import type { Submission, SubmissionSummary, ValidationStatus } from './submissionApi';
import type { TextLocationsResponse } from './fileApi';
import type { ChatSession, ChatHistoryResponse } from './chatApi';

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiService {
  userId: string | null = null;

  private async request<T>(
    endpoint: string,
    options?: RequestInit & { retries?: number },
  ): Promise<T> {
    const method = (options?.method ?? 'GET').toUpperCase();
    const isIdempotent = method === 'GET' || method === 'HEAD';
    const maxAttempts = Math.max(1, options?.retries ?? (isIdempotent ? 3 : 1));

    let lastError: unknown;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (attempt > 1) {
        await new Promise((r) => setTimeout(r, 500 * 2 ** (attempt - 2)));
      }
      try {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          ...options,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...options?.headers,
          },
        });
        if (response.ok) return response.json();

        const errorData = await response
          .json()
          .catch(() => ({ detail: response.statusText }));
        const err = new ApiError(
          errorData.detail || `API Error: ${response.statusText}`,
          response.status,
        );
        const retryable =
          isIdempotent &&
          (err.status === 408 || err.status === 429 || err.status >= 500);
        if (!retryable) throw err;
        lastError = err;
      } catch (e) {
        if (e instanceof ApiError) throw e;
        if (!isIdempotent) throw e;
        lastError = e;
      }
    }
    throw lastError ?? new Error('Request failed after retries');
  }

  async createSubmission(submissionType: string, deadline: string, name?: string): Promise<Submission> {
    return this.request('/submissions', {
      method: 'POST',
      body: JSON.stringify({ submission_type: submissionType, deadline, name, user_id: this.userId }),
    });
  }

  async getSubmission(id: string): Promise<Submission> {
    return this.request(`/submissions/${id}`);
  }

  async getAllSubmissions(skip = 0, limit = 50): Promise<{ total: number; items: Submission[]; skip: number; limit: number }> {
    return this.request(`/submissions?skip=${skip}&limit=${limit}`);
  }

  async getDocuments(): Promise<DocumentRow[]> {
    return this.request('/documents');
  }

  async getSubmissionsSummary(skip = 0, limit = 200): Promise<{ total: number; items: SubmissionSummary[]; skip: number; limit: number }> {
    return this.request(`/submissions/summary?skip=${skip}&limit=${limit}`);
  }

  async uploadFile(submissionId: string, slotId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(
      `${API_BASE_URL}/submissions/${submissionId}/slots/${slotId}/upload`,
      { method: 'POST', body: formData, credentials: 'include' }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || `Upload failed: ${response.statusText}`);
    }
    return response.json();
  }

  // ---------------------------------------------------------------------------
  // PRESIGNED-URL UPLOAD — bypasses Lambda's 6 MB payload limit
  // ---------------------------------------------------------------------------

  /** Step 1: get a presigned S3 PUT URL + pre-allocated file_id for a slot. */
  async getSlotUploadUrl(
    submissionId: string,
    slotId: string,
    filename: string,
    contentType: string,
    fileSize: number,
  ): Promise<{ file_id: string; upload_url: string; s3_key: string; expires_in: number }> {
    return this.request(`/submissions/${submissionId}/slots/${slotId}/upload-url`, {
      method: 'POST',
      body: JSON.stringify({ filename, content_type: contentType, file_size: fileSize }),
    });
  }

  /**
   * Step 2: PUT the file directly to S3 using the presigned URL.
   * Accepts an optional onProgress callback (0–100) for upload progress UI.
   */
  async uploadDirectToS3(
    presignedUrl: string,
    file: File,
    onProgress?: (pct: number) => void,
    contentType?: string,
  ): Promise<void> {
    // Read the file into an ArrayBuffer on the main thread before handing it to XHR.
    // Sending a File object directly can trigger an internal FileReaderSync call in a
    // browser worker thread, which fails with "file not found" when the file handle
    // is no longer accessible (e.g. after a drag-and-drop event is processed).
    const buffer = await file.arrayBuffer();

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl, true);
      // Content-Type is not a signed header in the presigned URL, so any value is accepted.
      // We still set it so S3 stores the correct MIME type on the object.
      const ct = contentType || file.type;
      if (ct) xhr.setRequestHeader('Content-Type', ct);

      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      };
      xhr.onerror = () => reject(new Error('S3 upload failed: network error'));
      xhr.send(buffer);
    });
  }

  /** Step 3: notify backend to validate & persist the file that is now in S3. */
  async confirmSlotUpload(
    submissionId: string,
    slotId: string,
    fileId: string,
    filename: string,
    fileSize: number,
    contentType: string,
  ) {
    return this.request(`/submissions/${submissionId}/slots/${slotId}/confirm-upload`, {
      method: 'POST',
      body: JSON.stringify({ file_id: fileId, filename, file_size: fileSize, content_type: contentType }),
    });
  }

  /** Step 1 (ZIP): get a presigned S3 PUT URL for a ZIP bulk upload. */
  async getZipUploadUrl(
    submissionId: string,
    filename: string,
    fileSize: number,
  ): Promise<{ upload_id: string; upload_url: string; s3_key: string; expires_in: number }> {
    return this.request(`/submissions/${submissionId}/upload-zip-url`, {
      method: 'POST',
      body: JSON.stringify({ filename, file_size: fileSize }),
    });
  }

  /** Step 3 (ZIP): notify backend to download the ZIP from S3 and propose mappings. */
  async confirmZipUpload(submissionId: string, uploadId: string, s3Key: string, filename: string) {
    return this.request(`/submissions/${submissionId}/confirm-zip-upload`, {
      method: 'POST',
      body: JSON.stringify({ upload_id: uploadId, s3_key: s3Key, filename }),
    });
  }

  /** Step 2 (single PDF): notify backend to download the PDF from S3 and propose mapping. */
  async confirmSingleUpload(submissionId: string, uploadId: string, filename: string) {
    return this.request(`/submissions/${submissionId}/confirm-single-upload`, {
      method: 'POST',
      body: JSON.stringify({ upload_id: uploadId, filename }),
    });
  }

  // Legacy multipart ZIP upload (kept for local-dev fallback)
  async uploadZip(submissionId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(
      `${API_BASE_URL}/submissions/${submissionId}/upload-zip`,
      { method: 'POST', body: formData, credentials: 'include' }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || `ZIP upload failed: ${response.statusText}`);
    }
    return response.json();
  }

  async confirmZipMappings(submissionId: string, proposalId: string, proposals: any[]) {
    const response = await fetch(
      `${API_BASE_URL}/submissions/${submissionId}/zip-proposals/${proposalId}/confirm`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ proposals }),
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || `API Error: ${response.statusText}`);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
  }

  async getValidationStatus(submissionId: string): Promise<ValidationStatus> {
    return this.request(`/submissions/${submissionId}/validation-status`);
  }

  async getValidationLogs(submissionId: string, requirementId?: string) {
    const params = new URLSearchParams({ limit: '1000' });
    if (requirementId) params.set('requirement_id', requirementId);
    return this.request(`/submissions/${submissionId}/validation-logs?${params}`);
  }

  async getAuditLogs(submissionId: string, limit = 200, offset = 0) {
    return this.request(`/submissions/${submissionId}/audit/logs?limit=${limit}&offset=${offset}`);
  }

  async getAuditStatistics(submissionId: string) {
    return this.request(`/submissions/${submissionId}/audit/statistics`);
  }

  async getGlobalAuditLogs(params?: {
    startDate?: string;
    endDate?: string;
    actor?: string;
    actionType?: string;
    submissionId?: string;
  }) {
    const query = new URLSearchParams();
    if (params?.startDate) query.set('start_date', params.startDate);
    if (params?.endDate) query.set('end_date', params.endDate);
    if (params?.actor) query.set('actor', params.actor);
    if (params?.actionType) query.set('action_type', params.actionType);
    if (params?.submissionId) query.set('submission_id', params.submissionId);
    const qs = query.toString();
    return this.request(`/audit/global${qs ? `?${qs}` : ''}`);
  }

  async listFilesBySlot(submissionId: string, slotId: string) {
    return this.request(`/submissions/${submissionId}/slots/${slotId}/files`);
  }

  async getNotifications(unreadOnly: boolean = false) {
    return this.request(`/notifications?unread_only=${unreadOnly}`);
  }

  async markNotificationRead(notificationId: string) {
    return this.request(`/notifications/${notificationId}/read`, { method: 'POST' });
  }

  async markAllNotificationsRead() {
    return this.request('/notifications/read-all', { method: 'POST' });
  }

  /** Fetch a binary file from an authenticated endpoint and trigger a browser download. */
  async downloadFile(endpoint: string, filename: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Download failed');
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  getPdfUrl(submissionId: string, fileId: string): string {
    return `${API_BASE_URL}/submissions/${submissionId}/files/${fileId}/pdf`;
  }

  async getPdfPresignedUrl(submissionId: string, fileId: string): Promise<{ url: string }> {
    return this.request(`/submissions/${submissionId}/files/${fileId}/pdf-url`);
  }

  async getTextLocations(
    submissionId: string,
    fileId: string,
    pageNumber: number,
    searchText: string
  ): Promise<TextLocationsResponse> {
    const params = new URLSearchParams({
      page_number: pageNumber.toString(),
      search_text: searchText,
    });
    return this.request(`/submissions/${submissionId}/files/${fileId}/text-locations?${params}`);
  }

  // ── Chat ─────────────────────────────────────────────────────────

  async sendChatMessage(submissionId: string, message: string, sessionId?: string): Promise<{ response: string; session_id: string; tool_calls?: any[] }> {
    return this.request(`/submissions/${submissionId}/chat`, {
      method: 'POST',
      body: JSON.stringify({ message, session_id: sessionId }),
    });
  }

  async getChatSessions(submissionId: string): Promise<ChatSession[]> {
    return this.request(`/submissions/${submissionId}/chat/sessions`);
  }

  async getChatHistory(submissionId: string, sessionId: string): Promise<ChatHistoryResponse> {
    return this.request(`/submissions/${submissionId}/chat/sessions/${sessionId}`);
  }

  async deleteChatSession(submissionId: string, sessionId: string): Promise<void> {
    await fetch(`${API_BASE_URL}/submissions/${submissionId}/chat/sessions/${sessionId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  }

  async deleteSubmission(submissionId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/submissions/${submissionId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new ApiError(errorData.detail || `API Error: ${response.statusText}`, response.status);
    }
  }

  // ── Admin User Management ─────────────────────────────────────────

  async getUsers(): Promise<AdminUser[]> {
    return this.request('/admin/users');
  }

  async createUser(payload: CreateUserPayload): Promise<AdminUser> {
    return this.request('/admin/users', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateUser(userId: string, payload: UpdateUserPayload): Promise<AdminUser> {
    return this.request(`/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deactivateUser(userId: string): Promise<void> {
    await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  }
}

export const api = new ApiService();
